import { useState, useCallback, useEffect } from "react";
import {
  useDeleteChannelMutation,
  useEnableChannelMutation,
  useDisableChannelMutation,
  useGetStudioQuery,
  useGetUserQuery,
  Channel,
  apiSlice,
  USER_TAG,
  STUDIO,
} from "../../../features/api/apiSlice";
import { useRoomContext } from "@livekit/components-react";
import { IConnectionsParams } from "./Studio.interface";
import { useSocketApi } from "../../../hooks/useSocketApi";
import { useAppDispatch } from "../../../app/hooks";

export default function useStudioChannel({ serverUrl, token }: IConnectionsParams) {
  const room = useRoomContext();
  const dispatch = useAppDispatch();

  const { data: studio } = useGetStudioQuery();
  const { data: user } = useGetUserQuery("");
  const [deleteChannel] = useDeleteChannelMutation();
  const [enableChannel] = useEnableChannelMutation();
  const [disableChannel] = useDisableChannelMutation();

  const [isShowAddChannelPopup, setIsShowAddChannelPopup] = useState(false);
  const [channelToEdit, setChannelToEdit] = useState<Channel | undefined>();
  const [channelToDelete, setChannelToDelete] = useState<Channel | undefined>();
  const [enableChannelError, setEnableChannelError] = useState('');
  const [isShowSettingsPopup, setIsShowSettingsPopup] = useState(false);
  const [isBroadcastOnline, setIsBroadcastOnline] = useState(false);
  const [studioStatus, setStudioStatus] = useState(studio?.status || "offline");

  const handleClosePopupAddChannel = useCallback(() => setIsShowAddChannelPopup(false), []);
  const handleClosePopupEditChannel = useCallback(() => setChannelToEdit(undefined), []);
  const handleClosePopupDelete = useCallback(() => setChannelToDelete(undefined), []);

  const handleDeleteChannel = useCallback(() => {
    if (channelToDelete) {
      deleteChannel(channelToDelete.id);
      handleClosePopupDelete();
    }
  }, [channelToDelete, deleteChannel]);

  const handleToggleEnabled = useCallback(
    async (channel: Channel) => {
      if (!studio?.broadcast.restream_templates) return;
      const settings = {
        broadcastId: studio.broadcast.id,
        channelId: channel.id,
      };

      if (channel.enabled) {
        await disableChannel(settings);
      } else {
        const result = await enableChannel(settings);
        if ('error' in result) {
          setEnableChannelError((result.error as any).data.error);
        }
      }
    },
    [ studio?.broadcast.restream_templates ]
  );

  const channelsProps = {
    channels: studio?.broadcast.restream_templates || [],
    onToggleEnabled: handleToggleEnabled,
    onEdit: setChannelToEdit,
    onDelete: setChannelToDelete,
    onAddNewChannel: () => setIsShowAddChannelPopup(true),
  };

  useEffect(() => {
    if (!studio) return;

    setIsBroadcastOnline(studio.status === "online");
  }, [studio]);

  useSocketApi({
    broadcastId: studio?.broadcast.id,
    studioId: studio?.id,
    onBroadcastStatusUpdated: ({ status }) => {
      setIsBroadcastOnline(status === "online");
      setStudioStatus(status);
    },
    onCheckTariffEnded(is_tariff_ended) {
      if (!is_tariff_ended) {
        return;
      }
      dispatch(apiSlice.util.invalidateTags([ USER_TAG, STUDIO ]));
    },
    onStudioUpdateStatus: (data) => {
      if (studio && data.studio_id === studio.broadcast.id) {
        setStudioStatus(data.status);
        setIsBroadcastOnline(data.status === "online");
      }
    },
  });

  useEffect(() => {
    if (!studio || !serverUrl || !token || !room) return;

    room.connect(serverUrl, token);
  }, [room, serverUrl, studio, token]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent<any>) => {
      const eventData = event.data;
      const isOriginValid = event.origin === process.env.REACT_APP_API_ENDPOINT;
      const isChannelAuth = eventData.channel_auth;

      if (!isOriginValid || !isChannelAuth) return;

      const isPlatformGG = eventData.platform === "gg";
      const restreamTemplate = eventData.restream_template;

      if (isPlatformGG && restreamTemplate) {
        setIsShowAddChannelPopup(false);
        setChannelToEdit(eventData.restream_template);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  return {
    channelsProps,
    isShowAddChannelPopup,
    isShowSettingsPopup,
    channelToEdit,
    channelToDelete,
    enableChannelError,
    handleClosePopupAddChannel,
    handleClosePopupEditChannel,
    handleClosePopupDelete,
    handleDeleteChannel,
    setEnableChannelError,
    setIsShowSettingsPopup,
    studio,
    user,
    isBroadcastOnline,
    studioStatus
  };
}
